import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Send, CheckCircle, Download, Eye, EyeOff, X, Sparkles, FileText } from 'lucide-react';
import { decisionDetail, statutLabels } from '../data/mockData';
import './DecisionDetail.css';

const entityColors: Record<string, { color: string; bg: string; label: string }> = {
  PARTIE: { color: '#E85D04', bg: '#FFF3E8', label: 'Partie' },
  LIEU: { color: '#2563EB', bg: '#EFF6FF', label: 'Lieu' },
  SOCIETE: { color: '#7C3AED', bg: '#F5F3FF', label: 'Société' },
  MONTANT: { color: '#059669', bg: '#ECFDF5', label: 'Montant' },
  CONSEIL: { color: '#DC2626', bg: '#FEF2F2', label: 'Conseil' },
};

const DecisionDetail: React.FC = () => {
  const navigate = useNavigate();
  const [showOriginal, setShowOriginal] = useState(false);
  const [keywords, setKeywords] = useState<string[]>(decisionDetail.motsCles);
  const [newKeyword, setNewKeyword] = useState('');

  const d = decisionDetail;
  const s = statutLabels[d.statut];

  const highlightEntities = (text: string) => {
    const regex = /\[([A-Z_]+?)(?:_\d+)?\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const entityType = match[1];
      const style = entityColors[entityType] || { color: '#666', bg: '#f0f0f0', label: entityType };
      parts.push(
        <span
          key={match.index}
          className="entity-tag"
          style={{ color: style.color, background: style.bg }}
        >
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter(k => k !== kw));
  };

  return (
    <div className="decision-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <button className="btn-back" onClick={() => navigate('/espace-cndj/decisions')}>
            <ArrowLeft size={18} />
          </button>
          <div className="detail-breadcrumb">
            <span className="breadcrumb-link" onClick={() => navigate('/espace-cndj/decisions')}>Décisions</span>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-current">{d.numero}</span>
          </div>
        </div>
        <div className="detail-header-actions">
          <button className="btn-detail-secondary"><Save size={14} /> Enregistrer</button>
          <button className="btn-detail-secondary"><Send size={14} /> Soumettre</button>
          <button className="btn-detail-primary"><CheckCircle size={14} /> Valider & publier</button>
          <button className="btn-detail-secondary"><Download size={14} /> Exporter</button>
        </div>
      </div>

      {/* 3-column layout */}
      <div className="detail-columns">
        {/* Left column: Metadata */}
        <div className="detail-col-left">
          <div className="detail-card">
            <h4 className="detail-card-title">Métadonnées</h4>
            <div className="meta-row">
              <span className="meta-label">Statut</span>
              <span className="status-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">N° Décision</span>
              <span className="meta-value">{d.numero}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Juridiction</span>
              <span className="meta-value">{d.juridiction}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Type</span>
              <span className="meta-value">{d.type}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Date</span>
              <span className="meta-value">{new Date(d.date).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Nature</span>
              <span className="meta-value">{d.nature}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Parties</span>
              <span className="meta-value">{d.parties}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Agent</span>
              <span className="meta-value">{d.agent}</span>
            </div>
          </div>

          <div className="detail-card">
            <h4 className="detail-card-title">Historique</h4>
            <div className="history-list">
              {d.historique.map((h, i) => (
                <div key={i} className="history-item">
                  <div className="history-dot" />
                  <div className="history-content">
                    <span className="history-action">{h.action}</span>
                    <span className="history-meta">{h.agent} · {new Date(h.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Center column: Document */}
        <div className="detail-col-center">
          <div className="detail-card document-card">
            <div className="document-header">
              <h4 className="detail-card-title">
                {showOriginal ? 'Document original' : 'Document anonymisé'}
              </h4>
              <div className="document-header-actions">
                <button className="btn-view-pdf">
                  <FileText size={14} /> Afficher le PDF
                </button>
                <button
                  className="btn-toggle-doc"
                  onClick={() => setShowOriginal(!showOriginal)}
                >
                  {showOriginal ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showOriginal ? "Masquer l'original" : "Voir l'original"}
                </button>
              </div>
            </div>

            {!showOriginal && (
              <div className="entity-legend">
                {Object.entries(entityColors).map(([key, val]) => (
                  <span key={key} className="entity-legend-item" style={{ color: val.color, background: val.bg }}>
                    {val.label}
                  </span>
                ))}
              </div>
            )}

            <div className="document-text">
              {showOriginal
                ? d.texteOriginal.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                  ))
                : d.texteAnonymise.split('\n').map((line, i) => (
                    <p key={i}>{line ? highlightEntities(line) : '\u00A0'}</p>
                  ))
              }
            </div>
          </div>
        </div>

        {/* Right column: AI Analysis */}
        <div className="detail-col-right">
          <div className="detail-card">
            <h4 className="detail-card-title">
              <Sparkles size={16} className="ia-icon" /> Analyse IA
            </h4>

            <div className="ia-section">
              <h5 className="ia-section-title">Résumé</h5>
              <p className="ia-resume">{d.resume}</p>
            </div>

            <div className="ia-section">
              <h5 className="ia-section-title">Mots-clés</h5>
              <div className="ia-keywords">
                {keywords.map(kw => (
                  <span key={kw} className="keyword-tag">
                    {kw}
                    <button className="keyword-remove" onClick={() => removeKeyword(kw)}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="keyword-add">
                <input
                  type="text"
                  placeholder="Ajouter un mot-clé"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                />
              </div>
            </div>

            <div className="ia-section">
              <h5 className="ia-section-title">Décisions similaires</h5>
              <div className="similar-list">
                {d.decisionsSimlaires.map((sim, i) => (
                  <div key={i} className="similar-item">
                    <div className="similar-info">
                      <span className="similar-numero">{sim.numero}</span>
                      <span className="similar-meta">{sim.juridiction} · {sim.nature}</span>
                    </div>
                    <span className="similar-score">{sim.similarite}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionDetail;
